import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/* K&J (29/09/2026, entrée 252) : toute écriture panier passe par la file unique (layout/theme.liquid),
   une requête à la fois, dans l'ordre des clics. Sans la file (page sans le layout) : exécution directe. */
const kjFile = (job) => (window.kjPanierFile ? window.kjPanierFile.run(job) : job());
import { processGifts, extractCartItemCollections } from '@theme/auto-gift-processor';

class CartDrawer extends HTMLElement {
  #rendering = false;
  #enAttente = null; // K&J (09/10) : dernier état reçu pendant un rendu, rendu juste après (avant : perdu)

  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);
    // K&J 2026-09-28 (entrée 231) : « Vider » du tiroir, par délégation (le tiroir est re-rendu).
    this.addEventListener('click', this.#onKjClearClick);

    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  // K&J 2026-09-28 (entrée 231) : premier clic = confirmation 8 s, second clic = /cart/clear.js
  // avec la section du tiroir, puis CartUpdatedEvent : tiroir, icône, compteurs et jauges suivent.
  #onKjClearClick = (event) => {
    const bouton = event.target.closest('[data-ref="kj-drawer-clear"]');
    if (!bouton) return;
    event.preventDefault();
    event.stopPropagation();
    if (!bouton.classList.contains('is-confirm')) {
      bouton.classList.add('is-confirm');
      bouton.textContent = bouton.dataset.confirm || bouton.textContent;
      clearTimeout(this.kjClearTimer);
      this.kjClearTimer = setTimeout(() => {
        bouton.classList.remove('is-confirm');
        bouton.textContent = bouton.dataset.text || bouton.textContent;
      }, 8000);
      return;
    }
    clearTimeout(this.kjClearTimer);
    bouton.disabled = true;
    // K&J (09/10, panier rapide) : dans la file (pas en parallèle d'un ajout en cours) et avec les compteurs de la page.
    const sections = window.kjPanierFile?.sections ? window.kjPanierFile.sections() : [this.dataset.sectionId];
    kjFile(() => fetch(`${Theme.routes.cart_url}/clear.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ sections: sections.join(','), sections_url: window.location.pathname }),
    })
      .then((r) => r.json())
      .then((reponse) => {
        const ev = new CartUpdatedEvent({}, { itemCount: 0, source: 'cart-drawer-clear', sections: reponse.sections });
        if (reponse.token && reponse.items) { ev.kjPanier = Promise.resolve(reponse); ev.kjFrais = true; }
        document.dispatchEvent(ev);
      }))
      .catch(() => window.location.reload());
  };

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    if (event.target.closest('dialog[open]')) return;

    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  #updateAutoGiftsMapping(autoGiftsHtml) {
    if (!autoGiftsHtml) return;

    const parsed = new DOMParser().parseFromString(autoGiftsHtml, 'text/html');
    const newScript = parsed.querySelector('[data-ref="cart-item-collections"]');
    const currentScript = document.querySelector('[data-ref="cart-item-collections"]');
    if (newScript && currentScript) currentScript.textContent = newScript.textContent;
  }

  async renderCartContents(parsedState) {
    if (!parsedState?.sections?.[this.dataset.sectionId]) return; // événement sans notre section : rien à redessiner
    if (this.#rendering) {
      this.#enAttente = parsedState;
      return;
    }
    this.#rendering = true;

    try {
      const sectionId = this.dataset.sectionId;
      const newCartDrawer = new DOMParser().parseFromString(parsedState.sections[sectionId], 'text/html');

      const newAutoGiftsHtml = parsedState.sections?.['auto-gifts'];
      const newAutoGifts = newAutoGiftsHtml ? new DOMParser().parseFromString(newAutoGiftsHtml, 'text/html') : null;
      const newCollections = newAutoGifts ? extractCartItemCollections(newAutoGifts) : {};

      const giftResult = await processGifts(sectionId, newCollections);

      if (giftResult?.sections) {
        parsedState = { ...parsedState, sections: { ...parsedState.sections, ...giftResult.sections }, itemCount: giftResult.itemCount ?? parsedState.itemCount };
      }

      const finalCartDrawer = giftResult?.sections?.[sectionId]
        ? new DOMParser().parseFromString(giftResult.sections[sectionId], 'text/html')
        : newCartDrawer;

      const currentCartDrawer = this.querySelector('.cart-drawer__inner');
      currentCartDrawer.innerHTML = finalCartDrawer.querySelector('.cart-drawer__inner').innerHTML;

      this.#updateAutoGiftsMapping(parsedState.sections?.['auto-gifts']);

      setTimeout(() => {
        if (parsedState.itemCount == 0) {
          this.classList.add('cart-drawer--empty');
        } else {
          this.classList.remove('cart-drawer--empty');
        }
      });

      this.#syncCartIconCount(parsedState);
    } finally {
      this.#rendering = false;
      const suivant = this.#enAttente;
      this.#enAttente = null;
      if (suivant) this.renderCartContents(suivant);
    }
  }

  #syncCartIconCount(parsedState) {
    const cartIcon = document.querySelector('cart-icon');
    if (!cartIcon) return;

    const countEl = cartIcon.querySelector('[data-ref="cart-count"]');
    if (!countEl) return;

    if (typeof parsedState.itemCount === 'number') {
      countEl.textContent = parsedState.itemCount;
      return;
    }

    cartIcon.ensureCartCountIsCorrect();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  #updateLineItemQuantity(line, quantity) {
    this.#enableLoading();
    // K&J (09/10, panier rapide) : retrait = la ligne disparaît au clic (le rendu serveur confirme).
    if (parseInt(quantity, 10) === 0) this.classList.add('kj-retrait');
    // La box « Ma sélection » suit au clic, sans attendre la réponse du panier (1 à 4 s client connecté).
    if (this.dataset.key) document.dispatchEvent(new CustomEvent('kj:optimiste-ligne', { detail: { key: this.dataset.key, quantity } }));

    // K&J (29/09) : la ligne est visée par sa clé (une position change si une autre écriture passe avant).
    // K&J (09/10, panier rapide) : la réponse ramène aussi les compteurs « Ma sélection » de la page : la box suit le
    // tiroir dans la même requête (avant : son rendu était jeté comme périmé, la recette retirée y restait).
    const drawerId = this.closest('cart-drawer').dataset.sectionId;
    const cible = this.dataset.key ? { id: this.dataset.key } : { line };
    // Panier rapide (09/10) : écriture seule puis sections relues en parallèle (kjPanierFile.ecrire), 0,4 s de gagné.
    const envoi = () => (window.kjPanierFile?.ecrire
      ? window.kjPanierFile.ecrire(Theme.routes.cart_change_url, { ...cible, quantity }, ['auto-gifts'])
        .catch((e) => e.data || { errors: [e.message] })
      : fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/json` },
        body: JSON.stringify({ ...cible, quantity, sections: [drawerId, 'auto-gifts'].join(','), sections_url: window.location.pathname }),
      }).then((response) => response.json()));

    return kjFile(() => envoi()
      .then((parsedResponseText) => {

        if (parsedResponseText.errors || parsedResponseText.status >= 400) {
          // K&J (09/10) : « message » n'existait pas (ReferenceError) : l'erreur n'était jamais affichée.
          const message = parsedResponseText.description || parsedResponseText.message || [].concat(parsedResponseText.errors || []).join(', ');
          this.classList.remove('kj-retrait');
          window.kjPanierFile?.signaler();
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message } }));
          return;
        }

        const ev = new CartUpdatedEvent(
          {},
          {
            itemCount: parsedResponseText.item_count,
            source: 'cart-drawer-item',
            sections: parsedResponseText.sections,
          },
        );
        // change.js renvoie le panier complet : les cartes de la grille le lisent sans requête.
        if (parsedResponseText.token && parsedResponseText.items) { ev.kjPanier = Promise.resolve(parsedResponseText); ev.kjFrais = true; }
        document.dispatchEvent(ev);
      })
      .catch((e) => {
        console.error(e);
        this.classList.remove('kj-retrait');
        window.kjPanierFile?.signaler();
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      }));
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
